/**
 * paintMeter — pure-function workshop for the canvas meter's per-frame
 * paint path.
 *
 * Per A6 §useMeterRenderer Option A: stateless helpers that take the
 * `MeterGeometryBag` (canvas + config + rings + dial) as a parameter
 * and render one frame. The rAF loop in `useMeterRenderer.ts` owns
 * scheduling; this module owns drawing.
 *
 * Mirrors the `utils/canvas/meter/{config,rings,dial}.ts` precedent —
 * each function is a pure transformation from "what to draw" → "calls
 * into Canvas / ring / dial". Reads from `bag` only; never mutates it.
 */

import {
    drawRings,
    drawRingsCrossfade,
    drawMilestoneBloom,
} from "../../ui/meter/canvas/meter/rings.ts";
import { drawDial } from "../../ui/meter/canvas/meter/dial.ts";
import type { MeterGeometryBag } from "./meterGeometry.ts";
// value.js scalar-math authority (AV.W11b). `lerp(start, end, t)` — range
// first, then the fraction (the retired local hoist took `(t, start, end)`).
import { lerp } from "@mkbabb/value.js";

/**
 * W5-L1/L2 — a live phase-handoff descriptor the renderer threads into
 * the paint path. `blend` is `usePhaseTransition`'s eased ManualTimeline
 * progress (0→1); `fromPhase`/`toPhase` name the boundary endpoints. When
 * `blend` has settled to 1 (no handoff) the paint takes the plain
 * single-phase path — no per-frame cost between boundaries.
 */
export interface MeterHandoff {
    fromPhase: string | undefined;
    toPhase: string | undefined;
    blend: number;
}

/**
 * KEEP-with-DDR (AV.W11b suffusion — KEEP #1 of 3; see
 * docs/decisions/DDR-AV-W11B-SUFFUSION.md "Timing lane"). This hand-rolled
 * `sin³` breathing oscillator is NOT a hand-roll to retire onto keyframes.js:
 * keyframes.js 4.1 ships NO looping-oscillator / continuous-sweep primitive
 * (it animates A→B timelines, not an endless self-sustaining breath). A
 * `NumericAnimation` would either run once and stop or need a manual rAF
 * re-arm loop — exactly the consumer-side library-gap workaround inv-16
 * forbids. So the oscillator STAYS as the meter's own engine math, and the
 * gap is ROUTED as **ASK-KF-OSCILLATOR** (a looping-timeline / `Oscillator`
 * primitive) in coordination/ADOPTION-ASKS.staged.md. R-CONSUME on publish.
 *
 * Move 6 (the always-on breathing oscillator). Returns the dial
 * start-angle with a ±2° sweep over a 6s period.
 *
 * W12-κ — the sweep gains a subtle apex hesitation via a sin-cubed
 * shape (per X6 §4.6 ARC principle). Pure sine is symmetric: the
 * needle's angular velocity peaks at the midpoint and is zero at
 * apex/trough — but the apex is exactly the threshold; the eye
 * reads it as a turnaround, not as a held breath. Composing
 * `sin³(phase)` (the cube of sine — odd-power; preserves the sign +
 * the period) softens the linear-near-zero region and lingers
 * slightly longer at the extremes: the needle's apex now reads as
 * "breathing" rather than "ticking through". Reduced-motion users
 * see the dial parked stiffly.
 */
export function idleSweep(
    time: number,
    startAngle: number,
    reduced: boolean,
): number {
    if (reduced) return startAngle;
    const phase = (time / 6000) * 2 * Math.PI;
    const sweepRad = (2 * Math.PI) / 180;
    const s = Math.sin(phase);
    // sin³ preserves the ±1 peak so the ±2° amplitude is intact;
    // it slows the crossing through zero and lingers at the extremes.
    return startAngle + s * s * s * sweepRad;
}

/**
 * Cached PRM media-query reader. The `MediaQueryList` resolves once
 * (lazily, first call); `.matches` reads the live value — the browser
 * updates the list on a user preference change, so the rAF loop tracks
 * the truth without a fresh `matchMedia` call per frame.
 */
let prmList: MediaQueryList | null = null;

export function reducedMotion(): boolean {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    if (prmList === null) {
        prmList = window.matchMedia("(prefers-reduced-motion: reduce)");
    }
    return prmList.matches;
}

/**
 * Paint one frame onto the bag's canvas:
 *
 * - `stateName` undefined → idle path: phantom rings at low alpha
 *   (`drawRings` with stateName === undefined branch) + dial driven
 *   by the breathing oscillator.
 * - `stateName` defined → active path: ring set paints the active
 *   phase's three-shade ladder + needle lerps to `t` between
 *   `config.startAngle` and `config.endAngle`.
 *
 * Bails when the bag isn't fully initialised yet (back-nav zero-rect
 * recovery case; the resize observer reinstalls geometry once
 * `.meter-col` lifts).
 */
export function drawFrame(
    bag: MeterGeometryBag,
    stateName: string | undefined,
    t: number,
    time: number,
    handoff?: MeterHandoff,
    milestoneIntensity = 0,
): void {
    const { canvas, config, rings, dial } = bag;
    if (!canvas || !config || !rings || !dial) return;
    canvas.clear();

    // Mid-handoff (`blend < 1`) the rings cross-fade their hue between
    // the two boundary phases (W5-L2) so the dial colour glides in
    // lockstep with the CSS `--phase-color`. Settled, the plain
    // single-phase path paints — boundaries are the only frames that
    // pay the cross-fade cost.
    if (handoff && handoff.blend < 1 && stateName) {
        drawRingsCrossfade(
            canvas,
            rings,
            handoff.fromPhase,
            handoff.toPhase,
            handoff.blend,
            t,
        );
    } else {
        drawRings(canvas, rings, stateName, t);
    }

    // W5-C4 — the one-shot milestone bloom paints over the active arcs
    // when the needle settles within a phase. A no-op at intensity 0.
    drawMilestoneBloom(canvas, rings, stateName, milestoneIntensity);

    // Idle vs active: the active path follows the lerp'd progress
    // angle; idle path drives the breathing oscillator (move 6).
    const reduced = reducedMotion();
    const theta = stateName
        ? lerp(config.startAngle, config.endAngle, Math.max(t, 0))
        : idleSweep(time, config.startAngle, reduced);

    drawDial(canvas, dial, theta);
}
